/* eslint-disable @typescript-eslint/no-explicit-any */
import {Injectable} from '@angular/core';
import {AppConfigService} from '../../../../common/service/app-config.service';
import {MapService} from '../../../services/map.service';
import {AnnotationUtilityService} from '../../../services/features/annotation-utility.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';
import {concatMap, of, tap} from 'rxjs';
import {LabelExpressionService} from '../../../services/label-expression/label-expression.service';

@Injectable({
    providedIn: 'root'
})
export class EnlargementUtilService {
    constructor(
        private appConfigService: AppConfigService,
        private mapService: MapService,
        private annotationUtilService: AnnotationUtilityService,
        private spatialUtilService: SpatialUtilService,
        private labelExpressionService: LabelExpressionService,
        private esriModuleService: EsriModuleService
    ) {}

    title = 'EnlargementUtilService';

    formatEnlargementIds(enlargementIds) {
        if (enlargementIds.length === 0) {
            return null;
        }
        return enlargementIds
            .sort((a, b) => a - b)
            .map(function (enlargementId) {
                return '[' + enlargementId + ']';
            })
            .join();
    }

    getEnlargementIds(feature) {
        let enlargementIds = [];

        if (feature.attributes.enlargementids) {
            enlargementIds = feature.attributes.enlargementids
                .replace(/\[/g, '')
                .replace(/\]/g, '')
                .split(',')
                .map(v => parseInt(v));
        }

        return enlargementIds;
    }

    isWithinEnlargement(feature, enlargementId) {
        const enlargementIds = this.getEnlargementIds(feature);
        const enlargementIdIndex = enlargementIds.indexOf(enlargementId);
        return enlargementIdIndex !== -1;
    }

    removeEnlargementId(feature, enlargementId) {
        const enlargementIds = this.getEnlargementIds(feature);
        const enlargementIdIndex = enlargementIds.indexOf(enlargementId);
        if (enlargementIdIndex !== -1) {
            enlargementIds.splice(enlargementIdIndex, 1);
        }
        if (!feature.attributes.enlargementid) {
            feature.attributes.enlargementids = this.formatEnlargementIds(enlargementIds);
        }
    }

    addEnlargementId(feature, enlargementId) {
        const enlargementIds = this.getEnlargementIds(feature);
        const enlargementIdIndex = enlargementIds.indexOf(enlargementId);
        if (enlargementIdIndex === -1) {
            enlargementIds.push(enlargementId);
        }
        if (!feature.attributes.enlargementid) {
            feature.attributes.enlargementids = this.formatEnlargementIds(enlargementIds);
        }
    }

    isMapModeMainPlan() {
        const currentMapMode = this.mapService.getMapMode();
        return currentMapMode.type === 'mainPlan';
    }

    updateEnlargementIdFromFeatures(enlargementId, data) {
        if (data && data.length > 0) {
            data.forEach(feature => {
                if (feature.attributes['enlargementids']) {
                    this.removeEnlargementId(feature, enlargementId);
                }
            });
        }
    }

    updateEnlargementId(enlargementId, features) {
        if (features && features.length > 0) {
            //Update existing annotations
            features.forEach(feature => {
                this.addEnlargementId(feature, enlargementId);
            });
        }
    }

    copyAnnotations(array1, array2, annotation, annotationSetId) {
        const {id, parentid: parentId, featuretype: featureType} = annotation.attributes;

        if (!array1.some(v => v.attributes.featuretype === featureType && v.attributes.id === id)) {
            array1.push(annotation);
        }

        if (!array2.some(v => v.attributes.featuretype === featureType && (v.attributes.id === id || v.attributes.parentid === id))) {
            const enlargementAnnotation = new this.esriModuleService.Graphic(annotation.toJson());
            this.spatialUtilService.deleteAttrs(enlargementAnnotation.attributes, ['id']);
            enlargementAnnotation.attributes.visible = null; //Enlargement annotations visibility is always null
            enlargementAnnotation.attributes.parentid = parentId || id;
            enlargementAnnotation.attributes.attribute10 = annotationSetId;
            enlargementAnnotation.attributes.enlargementids = null;
            array2.push(enlargementAnnotation);
        }
    }

    getAnnotationGroupKey(annotation) {
        return annotation.attributes.attribute9 + annotation.attributes.attribute10;
    }

    getAnnotationGroupMap(annotations) {
        const annotationGroupMap = annotations.reduce((acc, annotation) => {
            const annotationGroupMapKey = this.getAnnotationGroupKey(annotation);
            if (annotationGroupMapKey) {
                if (!acc[annotationGroupMapKey]) {
                    acc[annotationGroupMapKey] = [];
                }
                acc[annotationGroupMapKey].push(annotation);
            }
            return acc;
        }, {});
        return annotationGroupMap;
    }

    findRelatedFeatures(layer, extent, enlargementFeature, layers, array1, array2, callback) {
        const guidMap = {};

        const featureLayerFeatures = layers.map(featureLayer =>
            this.spatialUtilService
                .getGraphics(featureLayer)
                .filter(
                    f =>
                        !(
                            featureLayer.id === 'polygonFeatureLayer' &&
                            f.attributes.type === this.appConfigService.getDesignProperties().SURROUND_POLYGON_TYPE
                        ) && extent.intersects(f.geometry)
                )
        );

        const relatedFeatures = featureLayerFeatures.reduce((acc, features) => {
            acc.push(...features);
            return acc;
        }, []);

        const allAnnotations = this.spatialUtilService.getGraphics(layer);

        const annotationGroupMap = this.getAnnotationGroupMap(allAnnotations);

        allAnnotations.forEach(annotation => {
            const annotationGroup = annotationGroupMap[this.getAnnotationGroupKey(annotation)] || [annotation];

            if (
                annotationGroup.some(annotationGroupItem => {
                    return (
                        extent.intersects(annotationGroupItem.geometry) ||
                        relatedFeatures.some(relatedFeature => {
                            return this.isAnnotationPropertyMatch(annotationGroupItem, relatedFeature, extent);
                        })
                    );
                })
            ) {
                if (!enlargementFeature || !this.isAnnotationAlreadyAdded(annotation, array1)) {
                    this.copyAnnotations(array1, array2, annotation, this.annotationUtilService.getAnnotationSetId(guidMap, annotation));
                }
            }
        });

        callback();
    }

    isAnnotationAlreadyAdded(annotation, existingAnnotations) {
        return existingAnnotations.some(item => {
            return item.attributes.id === annotation.attributes.parentid || item.attributes.parentid === annotation.attributes.id;
        });
    }

    isAnnotationPropertyMatch(annotation, intersectingFeature, extent) {
        // check feature and intersecting feature for attributes match - id alone is not sufficient
        switch (annotation.attributes.featuretype) {
            case 'line':
                return (
                    annotation.attributes.featureid &&
                    annotation.attributes.featureid === intersectingFeature.attributes.id &&
                    Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'frompointid') &&
                    Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'lineids')
                );
            case 'constructLine':
                return (
                    annotation.attributes.featureid &&
                    annotation.attributes.featureid === intersectingFeature.attributes.id &&
                    Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'contructtype')
                );
            case 'lot':
                return (
                    (annotation.attributes.childid &&
                        annotation.attributes.childid === intersectingFeature.attributes.id &&
                        intersectingFeature.attributes.lotid) ||
                    (annotation.attributes.featureid &&
                        annotation.attributes.featureid === intersectingFeature.attributes.id &&
                        Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'polygonids') &&
                        Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'totalarea') &&
                        this.isPolygonIntersection(annotation, intersectingFeature, extent))
                );
            case 'road':
                return (
                    annotation.attributes.childid &&
                    annotation.attributes.childid === intersectingFeature.attributes.id &&
                    intersectingFeature.attributes.roadid &&
                    this.isPolygonIntersection(annotation, intersectingFeature, extent)
                );
            case 'interest':
                return (
                    annotation.attributes.childid &&
                    annotation.attributes.childid === intersectingFeature.attributes.id &&
                    Object.prototype.hasOwnProperty.call(intersectingFeature.attributes, 'interestids')
                );
            default:
                return false;
        }
    }

    isPolygonIntersection(annotation, intersectingFeature, extent) {
        const polygons = this.mapService.getPolygonsByParentFeature(intersectingFeature);

        if (!polygons || polygons.length === 0) {
            return true;
        }

        return polygons.some(
            polygon => polygon.attributes.id === annotation.attributes.childid && polygon.geometry && extent.intersects(polygon.geometry)
        );
    }

    findAllAnnotationsWithinEnlargementPolygon(polygonGeometry, array1, array2, enlargementId, callback) {
        this.findAllFeaturesWithinPolygon(
            this.spatialUtilService.getFeatureLayer('annotation'),
            f => {
                return f.attributes.enlargementid === enlargementId;
            },
            polygonGeometry,
            array1,
            array2,
            false,
            undefined,
            callback
        );
    }

    findAllAnglesWithinEnlargementPolygon(polygonGeometry, array1, array2, enlargementId, callback) {
        this.findAllFeaturesWithinPolygon(
            this.spatialUtilService.getFeatureLayer('angle'),
            f => {
                return f.attributes.enlargementid === enlargementId;
            },
            polygonGeometry,
            array1,
            array2,
            false,
            undefined,
            callback
        );
    }

    findAllAnnotationsWithinPolygon(polygonGeometry, array1, array2, callback) {
        this.findAllFeaturesWithinPolygon(
            this.spatialUtilService.getAnnotationMapLayer(),
            f => !f.attributes.enlargementid && !f.attributes.parentid,
            polygonGeometry,
            array1,
            array2,
            false,
            undefined,
            callback
        );
    }

    findAllAnglesForEnlargementCreation(polygonGeometry, array1, array2) {
        this.spatialUtilService
            .getAngles()
            .filter(a => !a.attributes.enlargementid && !a.attributes.parentid && a.geometry && polygonGeometry.contains(a.geometry))
            .forEach(a => this.copyAnnotations(array1, array2, a, this.annotationUtilService.getAnnotationSetId({}, a)));
    }

    findAllAnglesForEnlargementUpdate(polygonGeometry, array1, array2, enlargementId, mainPlanScale, oldScale, newScale) {
        const parentIds = this.spatialUtilService
            .getAngles()
            .filter(a => a.attributes.enlargementid === enlargementId)
            .map(a => a.attributes.parentid);

        const points = this.spatialUtilService.getPoints();
        const pointIds = points.map(p => p.attributes.id);

        this.spatialUtilService
            .getAngles()
            .filter(
                a =>
                    a.geometry &&
                    this.mapService.checkIntersect(polygonGeometry, a.geometry) &&
                    pointIds.includes(a.attributes.pointid) &&
                    (a.attributes.enlargementid === enlargementId ||
                        (!a.attributes.enlargementid && !a.attributes.parentid && !parentIds.includes(a.attributes.id)))
            )
            .forEach(a => {
                const sourcePoint = points.find(p => p.attributes.id === a.attributes.pointid);

                const pointGeometry = sourcePoint.geometry;

                const distance = Math.sqrt(Math.pow(a.geometry.x - pointGeometry.x, 2) + Math.pow(a.geometry.y - pointGeometry.y, 2));

                const bearing = Math.atan2(a.geometry.y - pointGeometry.y, a.geometry.x - pointGeometry.x);

                const scaleRatio = newScale / (a.attributes.enlargementid ? oldScale : mainPlanScale);

                const newFeature = new this.esriModuleService.Graphic(a.toJson());

                newFeature.geometry.x = newFeature.attributes.x = pointGeometry.x + distance * scaleRatio * Math.cos(bearing);
                newFeature.geometry.y = newFeature.attributes.y = pointGeometry.y + distance * scaleRatio * Math.sin(bearing);

                if (polygonGeometry.contains(newFeature.geometry)) {
                    this.copyAnnotations(array1, array2, newFeature, this.annotationUtilService.getAnnotationSetId({}, a));
                }
            });
    }

    findAllFeaturesWithinPolygon(layer, fnFilter, polygonGeometry, array1, array2, flag, scaleRatio, callback) {
        const guidMap = {};
        const allFeatures = this.spatialUtilService.getFeatures(layer).filter(fnFilter);

        if (flag && scaleRatio) {
            const allPoints = this.spatialUtilService.getPoints().filter(fnFilter);

            allFeatures.forEach(feature => {
                const points = allPoints.filter(p => feature.attributes.pointid && p.attributes.id === feature.attributes.pointid);
                if (!points.length) {
                    console.error(
                        this.title + ' : findAllFeaturesWithinPolygon - Point feature for id: ' + feature.attributes.pointid + ' does not exist'
                    );
                    return;
                }

                const sourcePoint = points[0];
                const pointGeometry = sourcePoint.geometry;

                // scale the distance between the two points according to the change in map scale
                const distance = Math.sqrt(Math.pow(feature.geometry.x - pointGeometry.x, 2) + Math.pow(feature.geometry.y - pointGeometry.y, 2));
                const bearing = Math.atan2(feature.geometry.y - pointGeometry.y, feature.geometry.x - pointGeometry.x);
                const updatedX = pointGeometry.x + distance * scaleRatio * Math.cos(bearing);
                const updatedY = pointGeometry.y + distance * scaleRatio * Math.sin(bearing);

                if (!isNaN(updatedX) && !isNaN(updatedY)) {
                    const transformedFeature = new this.esriModuleService.Graphic(feature.geometry);
                    transformedFeature.geometry = window.structuredClone(feature.geometry);
                    transformedFeature.attributes = window.structuredClone(feature.attributes);

                    transformedFeature.geometry.x = updatedX;
                    transformedFeature.geometry.y = updatedY;

                    if (polygonGeometry.contains(transformedFeature.geometry)) {
                        this.copyAnnotations(array1, array2, feature, this.annotationUtilService.getAnnotationSetId(guidMap, feature));
                    }
                }
            });
        } else {
            allFeatures
                .filter(f => f.geometry && polygonGeometry.contains(f.geometry))
                .forEach(f => this.copyAnnotations(array1, array2, f, this.annotationUtilService.getAnnotationSetId(guidMap, f)));
        }

        callback();
    }

    updateEnlargementFeature(enlargementFeature, updateEnlargementCallback) {
        return this.updateEnlargementFeatures([enlargementFeature]).pipe(
            tap(() => {
                if (updateEnlargementCallback) {
                    updateEnlargementCallback();
                }
            })
        );
    }

    updateEnlargementFeatures(enlargements) {
        const graphics = enlargements.map(enlargement => {
            const graphic =new this.esriModuleService.Graphic();
            graphic.setAttributes(enlargement.attributes);
            graphic.setGeometry(enlargement.geometry);
            return graphic;
        });

        return this.spatialUtilService.applyEdits('enlargement', null, graphics, null).pipe(
            concatMap(res => {
                const updates = (res as any).updates;
                if (!updates || updates.length === 0) {
                    return of([]);
                }

                const annotations = this.spatialUtilService
                    .getAnnotations()
                    .filter(a => a.attributes.featuretype === 'enlargement')
                    .map(a => {
                        const g = graphics.find(g => g.attributes.id === a.attributes.enlargementid && g.attributes.name !== a.attributes.attribute1);

                        if (g) {
                            const aJson = a.toJson();
                            const graphic = new this.esriModuleService.Graphic({attributes: aJson.attributes, geometry: aJson.geometry});
                            graphic.attributes.attribute1 = g.attributes.name;
                            graphic.attributes.labelexpression = this.labelExpressionService.create(graphic.attributes);
                            return graphic;
                        }
                    })
                    .filter(g => g);
                if (annotations.length) {
                    return this.spatialUtilService.applyEdits('annotation', null, annotations, null);
                } else {
                    return of([]);
                }
            })
        );
    }
}
